// src/components/media/MediaFileImage.jsx
//
// Shared protected media renderer.
//
// PURPOSE
// -------
// ProductPage, ProductOverviewPage, ServicePage and ServiceOverviewPage should
// all render protected media through this component.
//
// It loads the file through apiClient as Blob, creates an object URL, and then
// renders a normal <img> using the local object URL.
//
// This avoids auth/session problems caused by raw <img src="/media/...">.

import React, { useEffect, useState } from "react";
import { fetchMediaBlob } from "../../services/mediaFileService";

export default function MediaFileImage({
  media,
  alt,
  className = "",
  fallbackClassName = "",
  emptyText = "—",
  loadingText = "...",
  failedText = "Image",
}) {
  const [src, setSrc] = useState("");
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let objectUrl = "";

    async function loadImage() {
      setSrc("");
      setFailed(false);

      if (!media?.file_url) {
        return;
      }

      try {
        const blob = await fetchMediaBlob(media);

        if (cancelled) return;

        objectUrl = URL.createObjectURL(blob);
        setSrc(objectUrl);
      } catch {
        if (!cancelled) {
          setFailed(true);
        }
      }
    }

    loadImage();

    return () => {
      cancelled = true;

      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [media?.id, media?.file_url, media?.updatedAt, media?.file_name]);

  if (!media) {
    return <div className={fallbackClassName}>{emptyText}</div>;
  }

  if (failed) {
    return <div className={fallbackClassName}>{failedText}</div>;
  }

  if (!src) {
    return <div className={fallbackClassName}>{loadingText}</div>;
  }

  return (
    <img
      className={className}
      src={src}
      alt={alt || media.original_name || "Media file"}
      loading="lazy"
    />
  );
}